import { useMemo } from 'react';

import { send } from '@actual-app/core/platform/client/connection';
import { q } from '@actual-app/core/shared/query';

import { comprobarPlan } from '#components/mobile/plan/cerrojoPlan';
import { useQuery } from '#hooks/useQuery';
import { aqlQuery } from '#queries/aqlQuery';

import { cambiosCategorizar, sugerenciasPorBeneficiario } from './revisar';
import type { MovimientoPendiente, ParteDividida } from './revisar';

/**
 * Movimientos por revisar (`reviewed = false`: llegaron del banco o de un
 * archivo y no se han aprobado), en vivo. Los divididos cuentan una vez (el
 * padre).
 */
export function usePorRevisar() {
  const { data, isLoading } = useQuery<MovimientoPendiente>(
    () =>
      q('transactions')
        .filter({ reviewed: false })
        .options({ splits: 'none' })
        .select([
          'id',
          'date',
          'amount',
          'payee',
          'category',
          'account',
          'is_parent',
          'transfer_id',
        ])
        .orderBy([{ date: 'desc' }, { sort_order: 'desc' }]),
    [],
  );
  // Partes de los divididos pendientes: hacen falta para saber si se pueden
  // aprobar (todas con categoría o traspaso).
  const padres = useMemo(
    () => (data ?? []).filter(m => m.is_parent).map(m => m.id),
    [data],
  );
  const clavePadres = padres.join('|');
  const { data: partes } = useQuery<{
    parent_id: string;
    category: string | null;
    transfer_id: string | null;
  }>(
    () =>
      padres.length === 0
        ? null
        : q('transactions')
            .filter({ parent_id: { $oneof: padres } })
            .options({ splits: 'all' })
            .select(['parent_id', 'category', 'transfer_id']),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- la clave resume la lista
    [clavePadres],
  );
  const pendientes = useMemo(() => {
    const porPadre = new Map<string, ParteDividida[]>();
    for (const p of partes ?? []) {
      const lista = porPadre.get(p.parent_id) ?? [];
      lista.push({ category: p.category, transfer_id: p.transfer_id });
      porPadre.set(p.parent_id, lista);
    }
    return (data ?? []).map(m =>
      m.is_parent ? { ...m, partes: porPadre.get(m.id) ?? [] } : m,
    );
  }, [data, partes]);
  return { pendientes, cargando: isLoading };
}

/** Solo el número (para «Por hacer» y la insignia de la pestaña). */
export function useNumeroPorRevisar(): number {
  return usePorRevisar().pendientes.length;
}

/**
 * Categoría sugerida para los pendientes sin categoría: la del último
 * movimiento ya revisado y categorizado del mismo beneficiario.
 */
export function useSugerencias(pendientes: readonly MovimientoPendiente[]) {
  const beneficiarios = useMemo(
    () => [
      ...new Set(
        pendientes
          .filter(p => !p.category && !p.is_parent && p.payee)
          .map(p => p.payee as string),
      ),
    ],
    [pendientes],
  );
  const clave = beneficiarios.join('|');
  const { data } = useQuery<{
    payee: string | null;
    category: string | null;
    date: string;
  }>(
    () =>
      beneficiarios.length === 0
        ? null
        : q('transactions')
            .filter({
              payee: { $oneof: beneficiarios },
              category: { $ne: null },
              reviewed: true,
            })
            .select(['payee', 'category', 'date'])
            .orderBy({ date: 'desc' })
            .limit(500),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- la clave resume la lista
    [clave],
  );
  return useMemo(() => sugerenciasPorBeneficiario(data ?? []), [data]);
}

/**
 * Aprueba (y acepta la categoría sugerida si la había). Con `aprender`, la
 * categoría elegida enseña a la regla del comercio (lo pasado no se toca).
 */
export async function aprobar(
  cambios: { id: string; reviewed: true; category?: string }[],
  aprender = false,
) {
  if (cambios.length > 0 && comprobarPlan()) {
    await send('transactions-batch-update', {
      updated: cambios,
      learnCategories: aprender,
    });
  }
}

/**
 * Pone una categoría y aprueba (H78), en un solo update: «Deshacer» revierte
 * las dos cosas. Los divididos se saltan. Devuelve cuántos cambió.
 */
export async function categorizar(
  pendientes: readonly MovimientoPendiente[],
  ids: ReadonlySet<string>,
  category: string,
  aprender = false,
): Promise<number> {
  if (!comprobarPlan()) {
    return 0;
  }
  const updated = cambiosCategorizar(pendientes, ids, category);
  if (updated.length > 0) {
    await send('transactions-batch-update', {
      updated,
      learnCategories: aprender,
    });
  }
  return updated.length;
}

/** Borra los elegidos (con sus partes si son divididos). */
export async function borrar(ids: ReadonlySet<string>) {
  if (ids.size === 0 || !comprobarPlan()) {
    return;
  }
  const { data } = await aqlQuery(
    q('transactions')
      .filter({ parent_id: { $oneof: [...ids] } })
      .options({ splits: 'all' })
      .select(['id']),
  );
  const hijos = data as { id: string }[];
  await send('transactions-batch-update', {
    deleted: [...ids, ...hijos.map(h => h.id)].map(id => ({ id })),
  });
}
